import React from 'react';
import { Howler } from 'howler';

import {
  ModalLayout,
  ForwardRefHandlers,
} from '@/components/_modals/ModalLayout';
import EdgeButton from '@/components/_buttons/EdgeButton';
import { useTranslation } from 'next-i18next';
import useAskCount from '@/hooks/stores/useAskCountStore';
import { toast } from 'react-toastify';

interface ChargeModalProps {
  onClose: () => void;
}

export default function ChargeModal({ onClose }: ChargeModalProps) {
  const ref = React.useRef<ForwardRefHandlers>(null);
  const { askCount, setAskCount } = useAskCount();
  const { t } = useTranslation();
  const loadingToastId = React.useRef<string | number | null>(null);

  const handleCharge = () => {
    Howler.ctx?.suspend();
    window.flutter_inappwebview?.callHandler('showRewardedAd', '');
  };

  const handleClose = () => {
    ref.current?.close();
  };

  React.useEffect(() => {
    window.onChargeError = async () => {
      await Howler.ctx?.resume();
      toast.error(t('chargeModal.error'));
    };

    window.onWatchedReward = async (count: number) => {
      await Howler.ctx?.resume();
      setAskCount(count);
      handleClose();
    };

    window.onAdNotReady = async ({ isLoading, message }) => {
      await Howler.ctx?.resume();

      if (isLoading) {
        loadingToastId.current = toast.loading(t('chargeModal.loading'));
      } else {
        toast.error(message);
      }
    };

    window.onAdLoaded = async () => {
      await Howler.ctx?.resume();
      if (loadingToastId.current !== null) {
        toast.dismiss(loadingToastId.current);
        toast.success(t('chargeModal.loaded'));
        loadingToastId.current = null;
      }
    };

    window.onAdFailedToLoad = async () => {
      await Howler.ctx?.resume();
      toast.error(t('chargeModal.adFailedToLoad'));
    };

    window.onAdIncomplete = async () => {
      await Howler.ctx?.resume();
      handleClose();
    };
  }, []);

  return (
    <ModalLayout className="!h-auto" onClose={onClose}>
      <div className="p-6 text-white">
        <div className="mb-6 text-center text-lg">
          {t('chargeModal.description')}
        </div>
        <div className="mb-4 text-center text-xl font-bold">
          {t('chargeModal.remaining', {
            count: askCount,
            limit: 60,
          })}
        </div>
        <div className="flex justify-center">
          <EdgeButton onClick={handleCharge}>
            {t('chargeModal.chargeButton')}
          </EdgeButton>
        </div>
      </div>
    </ModalLayout>
  );
}
